/**
 * Data Portability Dialog — iter51.
 *
 * A single dialog with two tabs (Export / Import) launched from the
 * Employees Master. Import runs preview → user reviews per-row errors
 * → commits. Onboarding a new location becomes paste-and-go.
 */
import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DownloadSimple, UploadSimple, CheckCircle, WarningCircle,
  FileCsv, ArrowsClockwise, Table as TableIcon,
} from "@phosphor-icons/react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

async function downloadCsv(path, filename) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET", credentials: "include",
  });
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}


export default function DataPortabilityDialog({ open, onClose, onCommitted }) {
  const [tab, setTab] = useState("export");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="data-portability-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCsv size={18} weight="duotone" className="text-primary" />
            Employee Data Portability
          </DialogTitle>
          <DialogDescription className="text-xs">
            Bulk export the entire Employee Master to CSV, or import a CSV to onboard a whole
            location in one shot. Import runs a preview first — nothing is written until you commit.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 h-9">
            <TabsTrigger value="export" data-testid="portability-tab-export"><DownloadSimple size={12} className="mr-1" /> Export</TabsTrigger>
            <TabsTrigger value="import" data-testid="portability-tab-import"><UploadSimple size={12} className="mr-1" /> Import</TabsTrigger>
          </TabsList>

          <TabsContent value="export" className="mt-3 space-y-4">
            <ExportPanel />
          </TabsContent>

          <TabsContent value="import" className="mt-3 space-y-4">
            <ImportPanel onCommitted={() => { onCommitted?.(); onClose(); }} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------- Export ----------------------------

function ExportPanel() {
  const [downloading, setDownloading] = useState(false);
  const [approvalsDl, setApprovalsDl] = useState(false);

  const doDownload = async () => {
    setDownloading(true);
    try {
      await downloadCsv("/api/workforce/employees/export.csv",
        `employees-export-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("Export downloaded");
    } catch (e) { toast.error(e.message || "Export failed"); }
    finally { setDownloading(false); }
  };

  const doApprovalsExport = async () => {
    setApprovalsDl(true);
    try {
      await downloadCsv("/api/approvals/export.csv",
        `approvals-audit-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success("Approval audit trail downloaded");
    } catch (e) { toast.error(e.message || "Export failed"); }
    finally { setApprovalsDl(false); }
  };

  return (
    <div className="space-y-3">
      <div className="border rounded-md p-4 bg-muted/10 flex items-start gap-3">
        <TableIcon size={22} weight="duotone" className="text-primary shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-medium text-sm">Employees</h3>
          <p className="text-[11px] text-muted-foreground mt-0.5 max-w-lg">
            Downloads a CSV with every employee: identity, employment, workflow
            (Job Level + Reports To), bank details, and statutory fields. Compensation
            line items are managed in the UI — not in this export.
          </p>
        </div>
        <Button onClick={doDownload} disabled={downloading} data-testid="export-employees-btn">
          <DownloadSimple size={13} className="mr-1" /> {downloading ? "Preparing…" : "Download CSV"}
        </Button>
      </div>

      <div className="border rounded-md p-4 bg-muted/10 flex items-start gap-3">
        <TableIcon size={22} weight="duotone" className="text-primary shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-medium text-sm">Approval Audit Trail</h3>
          <p className="text-[11px] text-muted-foreground mt-0.5 max-w-lg">
            Every approval request&apos;s audit trail as CSV — one row per state
            transition (submitted, routed, approved, rejected, escalated, withdrawn).
            Owner/Admin only.
          </p>
        </div>
        <Button onClick={doApprovalsExport} disabled={approvalsDl} variant="outline" data-testid="export-approvals-btn">
          <DownloadSimple size={13} className="mr-1" /> {approvalsDl ? "Preparing…" : "Download CSV"}
        </Button>
      </div>
    </div>
  );
}


// ---------------------------- Import ----------------------------

function ImportPanel({ onCommitted }) {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = async () => {
    try {
      await downloadCsv("/api/workforce/employees/import/template.csv",
        "employees-import-template.csv");
      toast.success("Template downloaded");
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  const parseFile = async (file) => {
    setUploading(true);
    setCommitResult(null);
    setPreview(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/workforce/employees/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      if (data.total_errors > 0) {
        toast.warning(`Preview ready — ${data.total_errors} error(s) across ${data.with_errors} row(s). Fix them or exclude to commit.`);
      } else {
        toast.success(`Preview ready — ${data.total_rows} row(s) valid, ready to commit`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const commit = async () => {
    if (!preview) return;
    // Only rows without errors get sent. If ignoreErrors=false, we
    // block. If a mix, we still filter server-side.
    const committable = preview.rows.filter(r => (r.errors || []).length === 0);
    if (committable.length === 0) return toast.error("No committable rows.");
    setCommitting(true);
    try {
      const { data } = await api.post("/workforce/employees/import/commit", {
        rows: committable, dry_run: false,
      });
      setCommitResult(data);
      if (data.ok) {
        toast.success(`Import complete — created ${data.created}, updated ${data.updated}`);
        onCommitted?.();
      } else {
        toast.warning(`Committed with ${data.errors.length} error(s). See details below.`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCommitting(false); }
  };

  const reset = () => {
    setPreview(null); setCommitResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-3">
      <div className="border rounded-md p-3 bg-primary/5 border-primary/20 flex items-start gap-2 text-xs">
        <WarningCircle size={16} className="text-primary shrink-0 mt-0.5" />
        <div className="flex-1">
          <b>How to import:</b> Download the template → fill in employees → upload → review the
          preview → commit. Non-Owner employees <b>must</b> have <code>reports_to_employee_number</code>
          or <code>is_owner=true</code>.
        </div>
        <Button size="sm" variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
          <DownloadSimple size={12} className="mr-1" /> Template
        </Button>
      </div>

      {!preview && !commitResult && (
        <div className="border-2 border-dashed rounded-md p-8 text-center space-y-2 bg-muted/10">
          <UploadSimple size={32} className="mx-auto text-primary" weight="duotone" />
          <p className="text-sm">Drop a CSV or click to upload</p>
          <input
            ref={fileRef}
            type="file" accept=".csv,text/csv"
            className="hidden"
            onChange={e => e.target.files?.[0] && parseFile(e.target.files[0])}
            data-testid="import-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="upload-csv-btn"
          >
            <UploadSimple size={13} className="mr-1" />
            {uploading ? "Parsing…" : "Choose CSV file"}
          </Button>
        </div>
      )}

      {preview && !commitResult && (
        <PreviewTable
          preview={preview}
          onCommit={commit}
          onReset={reset}
          committing={committing}
        />
      )}

      {commitResult && (
        <CommitResult result={commitResult} onReset={reset} />
      )}
    </div>
  );
}


function PreviewTable({ preview, onCommit, onReset, committing }) {
  const goodRows = preview.rows.filter(r => (r.errors || []).length === 0);
  const badRows = preview.rows.filter(r => (r.errors || []).length > 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-2">
        <StatCard label="Total rows" value={preview.total_rows} />
        <StatCard label="Creates" value={preview.creates} className="text-emerald-700" />
        <StatCard label="Updates" value={preview.updates} className="text-blue-700" />
        <StatCard label="Errors" value={preview.total_errors} className={preview.total_errors ? "text-rose-700" : "text-emerald-700"} />
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 sticky top-0">
              <tr>
                <th className="text-left p-2">#</th>
                <th className="text-left p-2">Action</th>
                <th className="text-left p-2">Employee #</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Issues</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {preview.rows.map(r => (
                <tr key={r.row_index} className={r.errors.length > 0 ? "bg-rose-50" : "hover:bg-muted/30"} data-testid={`preview-row-${r.row_index}`}>
                  <td className="p-2 font-mono text-[10px] text-muted-foreground">{r.row_index}</td>
                  <td className="p-2">
                    <Badge variant="outline" className={r.action === "create" ? "border-emerald-300 text-emerald-700 text-[9px]" : "border-blue-300 text-blue-700 text-[9px]"}>
                      {r.action}
                    </Badge>
                  </td>
                  <td className="p-2 font-mono text-[10px]">{r.employee_number || "—"}</td>
                  <td className="p-2">{r.name || <i className="text-muted-foreground">(missing)</i>}</td>
                  <td className="p-2 text-muted-foreground">{r.email || "—"}</td>
                  <td className="p-2">
                    {r.errors.length === 0 && r.warnings.length === 0 && (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={11} /> OK</span>
                    )}
                    {r.errors.map((e, i) => (
                      <div key={`err-${r.row_index}-${i}`} className="text-rose-700 text-[10px]" data-testid={`row-error-${r.row_index}-${i}`}>• {e}</div>
                    ))}
                    {r.warnings.map((w, i) => (
                      <div key={`warn-${r.row_index}-${i}`} className="text-amber-700 text-[10px]" data-testid={`row-warning-${r.row_index}-${i}`}>• {w}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={onReset} disabled={committing} data-testid="reset-import-btn">
          <ArrowsClockwise size={12} className="mr-1" /> Upload a different file
        </Button>
        <div className="flex-1" />
        {badRows.length > 0 && (
          <span className="text-[11px] text-amber-700">
            {badRows.length} row(s) with errors will be skipped
          </span>
        )}
        <Button onClick={onCommit} disabled={committing || goodRows.length === 0} data-testid="commit-import-btn">
          {committing ? "Committing…" : `Commit ${goodRows.length} row(s)`}
        </Button>
      </div>
    </div>
  );
}


function CommitResult({ result, onReset }) {
  return (
    <div className="space-y-3">
      <div className="border rounded-md p-4 bg-emerald-50 border-emerald-200 flex items-start gap-2" data-testid="commit-result">
        <CheckCircle size={20} className="text-emerald-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="font-medium text-sm text-emerald-900">Import complete</div>
          <div className="text-xs text-emerald-800 mt-1">
            <b>{result.created}</b> created, <b>{result.updated}</b> updated
            {result.errors.length > 0 && <span>, <b className="text-rose-700">{result.errors.length}</b> failed</span>}
          </div>
        </div>
      </div>
      {result.errors.length > 0 && (
        <div className="border border-rose-200 rounded-md overflow-hidden">
          <div className="p-2 bg-rose-50 text-xs font-medium text-rose-900">Rows that failed at commit time</div>
          <div className="divide-y max-h-40 overflow-y-auto">
            {result.errors.map((e, i) => (
              <div key={`commit-err-${e.row_index}-${i}`} className="p-2 text-xs" data-testid={`commit-error-${i}`}>
                <div className="font-mono text-[10px] text-muted-foreground">Row {e.row_index} · {e.employee_number || "—"} · {e.name || "—"}</div>
                <div className="text-rose-700">{e.error}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <Button variant="outline" onClick={onReset} data-testid="import-again-btn">
        <ArrowsClockwise size={12} className="mr-1" /> Import another file
      </Button>
    </div>
  );
}


function StatCard({ label, value, className = "" }) {
  return (
    <div className="border rounded-md p-2.5 text-center">
      <div className={`text-lg font-semibold ${className}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
    </div>
  );
}
